import type { ComposeEmailOutput } from '@realadvisor/common-types';

import type { EmailFormSentDetails } from '@modules/_shared/components/email-form/types';
import {
  type ComposeEmailParentLink,
  type ComposeEmailTypeLabels,
  composeEmailParentPath,
  parseComposeEmailInput,
} from '@modules/_shared/hooks/composeEmailDraft';

// Pure helpers behind ComposeEmailToolCard: the tool result reported to the
// agent, and how a stored call is read back for the summary. Kept free of
// React/MUI imports so it runs under `pnpm test:node`.

export type ComposeEmailResultHandler = (
  toolCallId: string,
  output: ComposeEmailOutput,
) => void;

const normalizeWhitespace = (text: string): string =>
  text.replace(/\s+/g, ' ').trim();

export const computeEdited = (
  draft: { subject: string; bodyPlaintext: string | null },
  sent: EmailFormSentDetails | undefined,
): boolean | null => {
  if (draft.bodyPlaintext == null || sent == null) {
    return null;
  }
  return (
    normalizeWhitespace(draft.subject) !== normalizeWhitespace(sent.subject) ||
    normalizeWhitespace(draft.bodyPlaintext) !==
      normalizeWhitespace(sent.bodyPlaintext)
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

/** `emailMessage.id` of the email-service send response. */
export const sentEmailMessageId = (response: unknown): string | null => {
  const emailMessage = isRecord(response) ? response.emailMessage : null;
  const id = isRecord(emailMessage) ? emailMessage.id : null;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

/** `campaignId` of the campaign endpoint response (individual delivery). */
export const sentCampaignId = (response: unknown): string | null => {
  const id = isRecord(response) ? response.campaignId : null;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

export const buildSentOutput = ({
  response,
  sentRecipients,
  sent,
  draftSubject,
  draftBodyPlaintext,
}: {
  response: unknown;
  sentRecipients: ReadonlyArray<{ email: string }>;
  sent: EmailFormSentDetails | undefined;
  draftSubject: string;
  draftBodyPlaintext: string | null;
}): ComposeEmailOutput => ({
  status: 'sent',
  emailMessageId: sentEmailMessageId(response),
  campaignId: sentCampaignId(response),
  recipientCount: sentRecipients.length,
  subject: sent?.subject ?? draftSubject,
  to: sentRecipients.map(recipient => recipient.email),
  edited: computeEdited(
    { subject: draftSubject, bodyPlaintext: draftBodyPlaintext },
    sent,
  ),
});

export type ComposeEmailSummaryLink = Omit<ComposeEmailParentLink, 'globalId'>;

/**
 * Chips for a stored draft's records, from its input alone (no lookup). None
 * for individual delivery: the campaign logs each email on its recipient only,
 * never on the parents.
 */
export const composeEmailSummaryLinks = (
  input: unknown,
  typeLabels: ComposeEmailTypeLabels,
): ComposeEmailSummaryLink[] => {
  const parsed = parseComposeEmailInput(input);
  if (!parsed.ok || parsed.input.delivery === 'individual') {
    return [];
  }
  return (parsed.input.parents ?? []).map(parent => ({
    type: parent.type,
    id: parent.id,
    label: typeLabels[parent.type],
    path: composeEmailParentPath(parent),
  }));
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string');

/** Reads a stored tool output back; null when it is not a compose_email result. */
export const parseComposeEmailOutput = (
  output: unknown,
): ComposeEmailOutput | null => {
  if (!isRecord(output)) {
    return null;
  }
  switch (output.status) {
    case 'sent': {
      const to = isStringArray(output.to) ? output.to : [];
      return {
        status: 'sent',
        emailMessageId:
          typeof output.emailMessageId === 'string'
            ? output.emailMessageId
            : null,
        campaignId:
          typeof output.campaignId === 'string' && output.campaignId !== ''
            ? output.campaignId
            : null,
        recipientCount:
          typeof output.recipientCount === 'number' &&
          Number.isInteger(output.recipientCount) &&
          output.recipientCount >= 0
            ? output.recipientCount
            : to.length,
        subject: typeof output.subject === 'string' ? output.subject : '',
        to,
        edited: typeof output.edited === 'boolean' ? output.edited : null,
      };
    }
    case 'discarded':
      return { status: 'discarded' };
    case 'error':
      return {
        status: 'error',
        error:
          typeof output.error === 'string'
            ? output.error
            : JSON.stringify(output.error ?? null),
      };
    default:
      return null;
  }
};

export type ComposeEmailStepSummary =
  | { kind: 'sent'; subject: string }
  | { kind: 'mass-sent'; count: number }
  | { kind: 'discarded' }
  | { kind: 'not-sent' };

/** What an answered compose_email step row says once folded into the steps. */
export const composeEmailStepSummary = (part: {
  state?: string;
  output?: unknown;
}): ComposeEmailStepSummary => {
  const output =
    part.state === 'output-available'
      ? parseComposeEmailOutput(part.output)
      : null;
  switch (output?.status) {
    case 'sent':
      return output.campaignId != null
        ? { kind: 'mass-sent', count: output.recipientCount }
        : { kind: 'sent', subject: output.subject };
    case 'discarded':
      return { kind: 'discarded' };
    default:
      return { kind: 'not-sent' };
  }
};

export type ComposeEmailCardView =
  | 'drafting'
  | 'waiting'
  | 'inactive'
  | 'answered';

// `waiting` needs a result handler: only the chat's last turn can still be
// answered (the AI SDK writes a result into the last message only). An
// answered call normally renders as a step (messageSegments), not as a card.
export const composeEmailCardView = (
  state: string | undefined,
  canAnswer: boolean,
): ComposeEmailCardView => {
  switch (state) {
    case 'output-available':
    case 'output-error':
      return 'answered';
    case 'input-available':
      return canAnswer ? 'waiting' : 'inactive';
    default:
      return 'drafting';
  }
};
